import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useSession } from '@/lib/session';
import { useMnaCompanies } from '../lib/api';
import type {
  CompanyCardDto,
  CompanyFilter,
  DistressStage,
  OwnershipType,
  SizeBand,
  SubSegment,
  WatchlistStage,
} from '../types';
import {
  ConfidenceValue,
  DISTRESS_LABELS,
  DistressChip,
  Flag,
  OWNERSHIP_LABELS,
  OwnershipBadge,
  SIZE_BAND_LABELS,
  SIZE_BAND_SHORT,
  ScorePair,
  SUB_SEGMENT_LABELS,
  SubSegmentBadge,
  WATCHLIST_LABELS,
  WatchlistBadge,
  fmtRevenueUsdM,
} from './_shared';

// Tab 3 — Companies. Left sticky filter panel (WWD RigFleet pattern) over a card grid;
// filter state is a types.ts CompanyFilter so A6 can serialize it to /api/companies
// query params unchanged. Card click → the company dossier route.

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

const labelClass = 'text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)';

const COUNTRY_LABELS: Record<string, string> = {
  NO: 'Norway',
  GB: 'United Kingdom',
  US: 'United States',
  DE: 'Germany',
  SG: 'Singapore',
};

function countryLabel(cc: string): string {
  return COUNTRY_LABELS[cc] ?? cc;
}

type SortKey = 'fit' | 'availability' | 'name' | 'revenue' | 'country';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'fit', label: 'Fit score' },
  { key: 'availability', label: 'Availability score' },
  { key: 'name', label: 'Name' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'country', label: 'HQ country' },
];

function sortValue(c: CompanyCardDto, k: SortKey): string | number | null {
  switch (k) {
    case 'fit':
      return c.fitScore;
    case 'availability':
      return c.availabilityScore;
    case 'name':
      return c.name;
    case 'revenue':
      return c.revenueUsdM ?? null;
    case 'country':
      return c.hqCountry;
  }
}

/** Nulls always sort last; numeric sorts descend (best first), strings ascend. */
function compare(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return b - a;
  return String(a).localeCompare(String(b));
}

const EMPTY_FILTER: CompanyFilter = {};

function matches(c: CompanyCardDto, f: CompanyFilter): boolean {
  if (f.q) {
    const q = f.q.toLowerCase();
    if (!c.name.toLowerCase().includes(q) && !(c.oneLiner ?? '').toLowerCase().includes(q)) return false;
  }
  if (f.subSegment?.length && !c.subSegments.some((s) => f.subSegment!.includes(s))) return false;
  if (f.hqCountry?.length && (c.hqCountry === null || !f.hqCountry.includes(c.hqCountry))) return false;
  if (f.ownershipType?.length && !f.ownershipType.includes(c.ownershipType)) return false;
  if (f.sizeBand?.length && !f.sizeBand.includes(c.sizeBand)) return false;
  if (f.distressStage?.length && !f.distressStage.includes(c.distressStage)) return false;
  if (f.watchlistStage?.length && (c.watchlistStage === null || !f.watchlistStage.includes(c.watchlistStage))) return false;
  if (f.minFit !== undefined && f.minFit > 0 && (c.fitScore === null || c.fitScore < f.minFit)) return false;
  if (f.minAvailability !== undefined && f.minAvailability > 0 && (c.availabilityScore === null || c.availabilityScore < f.minAvailability))
    return false;
  return true;
}

export default function Companies() {
  const { data: session } = useSession();
  const isAdmin = !!session?.isAdmin;
  // Admins also fetch retired rows — they render ONLY in the collapsed section below.
  const { data, isLoading, error } = useMnaCompanies(isAdmin);
  const companies = useMemo(() => (data ?? []).filter((c) => c.status !== 'retired'), [data]);
  const retired = useMemo(() => (data ?? []).filter((c) => c.status === 'retired'), [data]);
  const [showRetired, setShowRetired] = useState(false);

  const [filter, setFilter] = useState<CompanyFilter>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortKey>('fit');

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of companies) if (c.hqCountry) set.add(c.hqCountry);
    return [...set].sort((a, b) => countryLabel(a).localeCompare(countryLabel(b)));
  }, [companies]);

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error) return <ErrorState message={(error as Error).message} />;

  const anyFilter = Object.values(filter).some((v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ''));

  const filtered = companies
    .filter((c) => matches(c, filter))
    .sort((a, b) => compare(sortValue(a, sort), sortValue(b, sort)) || a.name.localeCompare(b.name));

  const set = <K extends keyof CompanyFilter>(key: K, value: CompanyFilter[K]) =>
    setFilter((prev) => ({ ...prev, [key]: value }));

  /** Single-select UI over the contract's array fields (one-element array or undefined). */
  const setOne = <K extends 'hqCountry' | 'ownershipType' | 'sizeBand' | 'distressStage' | 'watchlistStage'>(
    key: K,
    value: string
  ) => set(key, (value === '' ? undefined : [value]) as CompanyFilter[K]);

  const toggleSubSegment = (s: SubSegment) =>
    setFilter((prev) => {
      const cur = prev.subSegment ?? [];
      const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
      return { ...prev, subSegment: next.length > 0 ? next : undefined };
    });

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Companies</CardTitle>
        <CardDescription className="mt-2">
          The scored target universe, one card per company — click a card for the full dossier.
          Estimated values render orange, undisclosed ones say so.
        </CardDescription>
      </Card>

      {companies.length === 0 ? (
        <Card>
          <span className="eyebrow">Coming up</span>
          <p className="mt-3 max-w-xl text-muted">
            No companies tracked yet — the universe fills as hunt sessions land. Filters and the
            dossier view are already wired up and waiting.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          {/* ── Left filter panel (sticky, scrolls independently) ── */}
          <aside className="w-full shrink-0 lg:w-64 lg:sticky lg:top-4">
            <div className="bg-surface border border-border rounded-(--radius-md) p-4 shadow-[var(--shadow-sm)] lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Filters</span>
                {anyFilter && (
                  <button
                    type="button"
                    onClick={() => setFilter(EMPTY_FILTER)}
                    className="text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="mt-4 flex flex-col gap-3">
                <label className="flex flex-col gap-1">
                  <span className={labelClass}>Search</span>
                  <input
                    type="search"
                    className={cn(selectClass, 'w-full')}
                    value={filter.q ?? ''}
                    placeholder="Name or description"
                    onChange={(e) => set('q', e.target.value === '' ? undefined : e.target.value)}
                  />
                </label>

                {/* Sub-segment multi-select */}
                <fieldset>
                  <legend className={labelClass}>Sub-segment</legend>
                  <div className="mt-1 flex flex-col gap-1.5">
                    {(Object.keys(SUB_SEGMENT_LABELS) as SubSegment[]).map((s) => (
                      <label key={s} className="flex items-center gap-2 text-sm text-text">
                        <input
                          type="checkbox"
                          checked={filter.subSegment?.includes(s) ?? false}
                          onChange={() => toggleSubSegment(s)}
                          className="h-4 w-4 accent-(--color-accent)"
                        />
                        {SUB_SEGMENT_LABELS[s]}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <PanelSelect label="HQ country" value={filter.hqCountry?.[0] ?? ''} onChange={(v) => setOne('hqCountry', v)}>
                  {countryOptions.map((cc) => (
                    <option key={cc} value={cc}>
                      {countryLabel(cc)}
                    </option>
                  ))}
                </PanelSelect>

                <PanelSelect
                  label="Ownership"
                  value={filter.ownershipType?.[0] ?? ''}
                  onChange={(v) => setOne('ownershipType', v)}
                >
                  {(Object.keys(OWNERSHIP_LABELS) as OwnershipType[]).map((o) => (
                    <option key={o} value={o}>
                      {OWNERSHIP_LABELS[o]}
                    </option>
                  ))}
                </PanelSelect>

                <PanelSelect label="Size band" value={filter.sizeBand?.[0] ?? ''} onChange={(v) => setOne('sizeBand', v)}>
                  {(Object.keys(SIZE_BAND_LABELS) as SizeBand[]).map((b) => (
                    <option key={b} value={b}>
                      {SIZE_BAND_LABELS[b]}
                    </option>
                  ))}
                </PanelSelect>

                <PanelSelect
                  label="Distress stage"
                  value={filter.distressStage?.[0] ?? ''}
                  onChange={(v) => setOne('distressStage', v)}
                >
                  {(Object.keys(DISTRESS_LABELS) as DistressStage[]).map((d) => (
                    <option key={d} value={d}>
                      {DISTRESS_LABELS[d]}
                    </option>
                  ))}
                </PanelSelect>

                <PanelSelect
                  label="Watchlist stage"
                  value={filter.watchlistStage?.[0] ?? ''}
                  onChange={(v) => setOne('watchlistStage', v)}
                >
                  {(Object.keys(WATCHLIST_LABELS) as WatchlistStage[]).map((w) => (
                    <option key={w} value={w}>
                      {WATCHLIST_LABELS[w]}
                    </option>
                  ))}
                </PanelSelect>

                <PanelNumber
                  label="Min fit score"
                  value={filter.minFit !== undefined ? String(filter.minFit) : ''}
                  onChange={(v) => set('minFit', v === '' ? undefined : Number(v))}
                  placeholder="e.g. 70"
                />
                <PanelNumber
                  label="Min availability"
                  value={filter.minAvailability !== undefined ? String(filter.minAvailability) : ''}
                  onChange={(v) => set('minAvailability', v === '' ? undefined : Number(v))}
                  placeholder="e.g. 50"
                />

                <label className="flex flex-col gap-1">
                  <span className={labelClass}>Sort by</span>
                  <select className={selectClass} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <p className="mt-4 border-t border-border pt-3 text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
                {filtered.length} of {companies.length} companies
              </p>
            </div>
          </aside>

          {/* ── Card grid ── */}
          <div className="min-w-0 flex-1">
            {filtered.length === 0 ? (
              <Card>
                <CardDescription>No companies match the current filters.</CardDescription>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {filtered.map((c) => (
                  <CompanyCard key={c.slug} company={c} />
                ))}
              </div>
            )}

            {/* ── Retired (admin-only, collapsed — Track A universe hygiene) ── */}
            {isAdmin && retired.length > 0 && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowRetired((v) => !v)}
                  className="text-sm text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
                >
                  {showRetired ? '▾' : '▸'} Retired ({retired.length}) — excluded from scoring and all surfaces;
                  un-retire from the dossier
                </button>
                {showRetired && (
                  <div className="mt-3 grid gap-4 opacity-70 md:grid-cols-2 2xl:grid-cols-3">
                    {retired.map((c) => (
                      <CompanyCard key={c.slug} company={c} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- pieces ---------- */

function PanelSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      <select className={cn(selectClass, 'w-full')} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">all</option>
        {children}
      </select>
    </label>
  );
}

function PanelNumber({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      <input
        type="number"
        min={0}
        max={100}
        inputMode="numeric"
        className={cn(selectClass, 'w-full')}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function CompanyCard({ company: c }: { company: CompanyCardDto }) {
  return (
    <Link
      to={`/mna/companies/${c.slug}`}
      className={cn(
        'flex flex-col bg-surface border border-border rounded-(--radius-md) p-5 shadow-[var(--shadow-sm)]',
        'transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {c.subSegments.map((s) => (
          <SubSegmentBadge key={s} segment={s} />
        ))}
        <span className="ml-auto flex items-center gap-1.5">
          <DistressChip stage={c.distressStage} />
          <WatchlistBadge stage={c.watchlistStage} />
        </span>
      </div>

      <h3 className="mt-2 flex items-center gap-2 text-lg font-semibold text-text">
        <Flag countryCode={c.hqCountry} />
        {c.name}
      </h3>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <OwnershipBadge ownershipType={c.ownershipType} peSponsor={c.peSponsor} />
        {c.peSponsor && <span className="text-xs text-muted">{c.peSponsor}</span>}
        {c.status !== 'active' && (
          <span className="rounded-(--radius-sm) border border-accent-danger px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-accent-danger">
            {c.status.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {c.oneLiner && <p className="mt-2 text-sm text-muted">{c.oneLiner}</p>}

      <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
        <ScorePair fit={c.fitScore} availability={c.availabilityScore} />
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">Revenue</dt>
          <dd className="mt-0.5 text-text">
            <ConfidenceValue
              value={c.revenueUsdM != null ? fmtRevenueUsdM(c.revenueUsdM) : null}
              confidence={c.revenueConfidence ?? (c.revenueUsdM != null ? 'confirmed' : 'not_disclosed')}
            />
          </dd>
        </div>
        <div>
          <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">Employees</dt>
          <dd className="mt-0.5 text-text [font-variant-numeric:tabular-nums]">
            {c.employeeCount !== null && c.employeeCount !== undefined ? c.employeeCount.toLocaleString('en-US') : '—'}
          </dd>
        </div>
        <div>
          <dt className="font-(family-name:--font-ui) text-[0.65rem] uppercase tracking-[0.12em] text-muted">Size</dt>
          <dd className="mt-0.5 text-text">{SIZE_BAND_SHORT[c.sizeBand]}</dd>
        </div>
      </dl>
    </Link>
  );
}
